import { Alert, Box, Grid, NumberInput, Skeleton, Stack, Table, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

import type { AccidentCalculations } from '../../../types/reports.js';
import { useAuth } from '../../../hooks/useAuth.js';
import { FormSubsection } from '../../ui/FormSubsection.js';
import { LabelWithInfo } from '../../ui/LabelWithInfo.js';
import {
  FieldWarningTooltip,
  ValidatedNumberInput,
  WARNING_ICON,
  WARNING_INPUT_STYLES,
} from '../../form/validatedFields.js';
import { DECIMAL_INPUT_PROPS, INTEGER_INPUT_PROPS } from '../../form/numberFieldPresets.js';
import { PreviousValueForField } from '../../form/PreviousValueLine.js';
import { useWarnings, type WarningsStore } from '../formStores.js';
import { RegisterFieldTab } from '../../form/RegisterFieldTab.js';
import {
  MATRIX_LABEL_TH,
  MATRIX_TABLE_STYLES,
  MATRIX_TOTAL_TH,
  MATRIX_VALUE_TH,
} from '../../form/matrixPresets.js';
import type { AccidentForm } from './accidentFormFields.js';

interface AccidentMatrixSectionProps {
  form: AccidentForm;
  /** Persisted/recomputed derived values, owned by the parent form. Display-only here. */
  calc: AccidentCalculations | null;
  /** Feldpfad -> Hinweistext der letzten Blur-Validierung (D-09/D-10). */
  warningsStore: WarningsStore;
  disabled?: boolean;
  loading?: boolean;
}

// Employee groups shown as columns (Operations removed).
const GROUP_COLUMNS = [
  { key: 'Wage', label: 'Lohnbelegschaft' },
  { key: 'Admin', label: 'Angestellte in der Verwaltung' },
  { key: 'Temp', label: 'Kräfte aus Arbeitnehmerüberlassung' },
] as const;

function fmtDecimal(v: string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '–';
  const n = parseFloat(v);
  if (Number.isNaN(n)) return '–';
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtInt(v: number | null | undefined): string {
  if (v === null || v === undefined) return '–';
  return v.toLocaleString('de-DE');
}

// A calculated, read-only decimal value rendered as a disabled input cell —
// matching the editable matrix inputs above and the Unfallhäufigkeitsrate fields.
//
// `warning` traegt den nicht-blockierenden Hinweis des Servers (D-09/D-13). Liegt einer an,
// bekommt die Zelle dieselbe Optik wie ein roter Fehlerzustand, nur in Gelb: Fuellung,
// Rahmen, Warn-Icon und der Text im Tooltip darueber. Faerbung und Icon kommen aus
// validatedFields, damit die Warnstufe an genau EINER Stelle definiert bleibt.
//
// Die Zelle bleibt in beiden Faellen readOnly UND disabled -- ein Hinweis darf einen
// berechneten Wert nicht bedienbar machen (T-20-08-03). `pointerEvents: 'none'` auf dem
// deaktivierten Feld ist die Voraussetzung dafuer, dass die Hover-Mechanik des geteilten
// Tooltips ueberhaupt greift: ein disabled <input> sendet keine Mausereignisse, der
// Box-Wrapper von FieldMessageTooltip saehe ohne diesen Durchgriff nie ein `mouseenter`.
// NICHT ENTFERNEN: seit Plan 20-15 zeigt sich keine Sprechblase mehr von selbst, Hover und
// Fokus sind die einzigen Ausloeser -- und eine deaktivierte Zelle bekommt keinen Fokus.
// Der Durchgriff ist damit der EINZIGE verbliebene Weg zu diesem Hinweistext.
//
// Es findet hier KEIN Schwellwertvergleich statt. Die Schwellen leben ausschliesslich als
// Constraint im Backend (Plan 20-06, AccidentData); das Formular zeigt nur, was der Server
// gemeldet hat. Sie konfigurierbar zu machen ist ein vorgemerkter, ausdruecklich nicht zu
// dieser Phase gehoerender Kandidat fuer den Mechanismus aus K5/D-18.
// `field` ist optional: nur die drei Jahresarbeitsstunden-Zellen sind persistierte Spalten der
// Vorjahresmeldung (D-15) und erhalten einen Vorwert; die KANUE-Zelle (zentraler Festwert, kein
// Feldpfad) ruft calcDecimalCell ohne dritten Parameter auf und bleibt unveraendert.
function calcDecimalCell(
  value: string | null | undefined,
  warning?: string,
  field?: string,
  label?: string
) {
  const parsed = value != null && value !== '' ? parseFloat(value) : NaN;
  const input = (
    <NumberInput
      value={Number.isNaN(parsed) ? '' : parsed}
      readOnly
      disabled
      leftSection={warning ? WARNING_ICON : undefined}
      styles={{
        input: warning
          ? { textAlign: 'right', ...WARNING_INPUT_STYLES, pointerEvents: 'none' }
          : { textAlign: 'right' },
      }}
      {...DECIMAL_INPUT_PROPS}
    />
  );

  return (
    <Table.Td>
      {warning ? <FieldWarningTooltip warning={warning}>{input}</FieldWarningTooltip> : input}
      {field ? <RegisterFieldTab field={field} label={label} /> : null}
      {field ? <PreviousValueForField field={field} /> : null}
    </Table.Td>
  );
}

export function AccidentMatrixSection({
  form,
  calc,
  warningsStore,
  disabled,
  loading,
}: AccidentMatrixSectionProps) {
  // D-14/D-15: "Interne Sicht" = ROLE_ADMIN oder ROLE_TRUSTEE. Nur diese Rollen sehen den
  // Gesamtwert der Jahresarbeitsstunden und die drei Berechnungs-Tooltips. Die Rolle wird hier
  // direkt per useAuth gelesen, nicht als Prop durch die Formularkette gereicht.
  // Hook-Aufruf steht bewusst vor dem loading-Early-Return (Rules of Hooks).
  const { user } = useAuth();
  const isInternalView = user?.roles?.some((r) => r === 'ROLE_ADMIN' || r === 'ROLE_TRUSTEE') ?? false;

  // Hinweise der letzten Blur-Validierung (D-09/D-10). Ebenfalls vor dem loading-Early-Return.
  // Die Traegerfelder sind berechnet und stehen nie im Client-Payload -- sie kommen
  // ausschliesslich vom Server (Plan 20-06), hier wird nichts nachgerechnet.
  const warnings = useWarnings(warningsStore);

  if (loading) {
    return (
      <Box>
        <Skeleton height={500} />
      </Box>
    );
  }

  // Reusable group-column cells for a decimal/integer matrix row.
  function inputCells(prefix: string, kind: 'decimal' | 'integer', rowLabel: string) {
    const props = kind === 'decimal' ? DECIMAL_INPUT_PROPS : INTEGER_INPUT_PROPS;
    return GROUP_COLUMNS.map((col) => (
      <Table.Td key={col.key}>
        <ValidatedNumberInput
          form={form}
          field={`${prefix}${col.key}`}
          fieldLabel={`${rowLabel} – ${col.label}`}
          kind={kind}
          align="right"
          disabled={disabled}
          {...props}
        />
      </Table.Td>
    ));
  }

  const tableHead = (
    <Table.Thead>
      <Table.Tr>
        <Table.Th style={MATRIX_LABEL_TH}>Kennzahl</Table.Th>
        {GROUP_COLUMNS.map((col) => (
          <Table.Th key={col.key} style={MATRIX_VALUE_TH}>
            {col.label}
          </Table.Th>
        ))}
        <Table.Th style={MATRIX_TOTAL_TH}>Gesamt</Table.Th>
      </Table.Tr>
    </Table.Thead>
  );

  return (
    <Stack gap="lg">
      {/* ---- Top table: hours worked + annual hours ---- */}
      <FormSubsection
        title="Unfallstatistik"
        description="Erfassen Sie die Unfallzahlen nach Beschäftigtengruppen. Einige Spalten und Felder werden automatisch berechnet."
      >
        <Box style={{ overflowX: 'auto' }}>
          <Table striped="odd" styles={MATRIX_TABLE_STYLES}>
            {tableHead}
            <Table.Tbody>
              <Table.Tr>
                <Table.Td><Text size="sm">Verfahrene Arbeitsstunden</Text></Table.Td>
                {inputCells('hoursWorked', 'decimal', 'Verfahrene Arbeitsstunden')}
                <Table.Td style={{ textAlign: 'right' }}>
                  <Text size="sm">{fmtDecimal(calc?.hoursWorkedTotal)}</Text>
                  <RegisterFieldTab field="hoursWorkedTotal" label="Verfahrene Arbeitsstunden – Gesamt" />
                  <PreviousValueForField field="hoursWorkedTotal" />
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  {/* D-17: Berechnungshinweis nur in der internen Sicht (K4) */}
                  {isInternalView ? (
                    <LabelWithInfo
                      label="Jahresarbeitsstunden"
                      info="Berechnet aus verfahrenen Arbeitsstunden ÷ Anzahl Mitarbeiter (Stunden pro Kopf). Der KANÜ-Wert wird zentral unter Einstellungen gepflegt."
                    />
                  ) : (
                    <Text size="sm">Jahresarbeitsstunden</Text>
                  )}
                </Table.Td>
                {/* K6/D-13: Hinweis an den beiden Zellen, die "verfahrene Stunden ÷ Mitarbeiter"
                    abbilden. Er kann erst greifen, wenn beides gefuellt ist -- die Mitarbeiterzahl
                    steht auf einem anderen Reiter, genau deshalb wertet der Server aus. */}
                {calcDecimalCell(calc?.annualHoursWage, warnings.annualHoursWage, 'annualHoursWage', 'Jahresarbeitsstunden – Lohnbelegschaft')}
                {calcDecimalCell(calc?.annualHoursAdmin, warnings.annualHoursAdmin, 'annualHoursAdmin', 'Jahresarbeitsstunden – Angestellte in der Verwaltung')}
                {/* Die KANUE-Zelle traegt den zentral gepflegten Festwert, der laut D-13 immer im
                    gruenen Bereich liegt und nicht vom Melder eingegeben wird -- kein Hinweis. */}
                {calcDecimalCell(calc?.annualHoursTemp, undefined, 'annualHoursTemp', 'Jahresarbeitsstunden – Kräfte aus Arbeitnehmerüberlassung')}
                {/* D-16: Gesamtwert nur in der internen Sicht (K3). Die Zelle selbst bleibt
                    immer bestehen, sonst verschiebt sich die Zeile gegen den Tabellenkopf. */}
                <Table.Td style={{ textAlign: 'right' }}>
                  {isInternalView && (
                    <>
                      <Text size="sm">{fmtDecimal(calc?.annualHoursTotal)}</Text>
                      <PreviousValueForField field="annualHoursTotal" />
                    </>
                  )}
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Box>
      </FormSubsection>

      {/* B-Neu (Plan 25.1-04): eigene Section unmittelbar unter der Jahresarbeitsstunden-Matrix,
          fuer den server-autoritativen gepoolten Durchschnitt (Plan 25.1-02/03). Melder sehen das
          Feld -- KEIN isInternalView-Gate um Section oder Feld selbst; D-16 gilt ausschliesslich
          fuer annualHoursTotal (siehe oben), nicht fuer dieses Feld (gesperrte Entscheidung aus
          25.1-CONTEXT.md).

          `calcDecimalCell()` kann hier NICHT wiederverwendet werden: die Funktion liefert ein
          <Table.Td> und ist an die Matrixtabelle gebunden, in einer Grid.Col waere das
          ungueltiges Markup. Die Hinweis-Verdrahtung von calcDecimalCell (siehe oben,
          Zeile 78-101) wird deshalb hier inline nachgebildet.

          `pointerEvents: 'none'` ist Pflicht und darf nicht wegoptimiert werden -- dieselbe
          Begruendung wie am ausfuehrlichen Kommentarblock bei calcDecimalCell oben: seit Plan
          20-15 oeffnet sich keine Sprechblase mehr von selbst, Hover und Fokus sind die
          einzigen Ausloeser, und ein disabled Eingabefeld bekommt keinen Fokus und sendet keine
          Mausereignisse. Der Durchgriff ist der EINZIGE Weg zu diesem Hinweistext.

          Es findet hier KEIN Schwellwertvergleich statt. Die Grenzen 1200/1800 leben
          ausschliesslich als Constraint im Backend; das Formular zeigt nur, was der Server
          gemeldet hat. */}
      <FormSubsection title="Durchschnittliche Jahresarbeitsstunden ohne KANÜ">
        <Grid>
          <Grid.Col span={{ base: 12, md: 4 }}>
            {(() => {
              const warning = warnings.annualHoursCombined;
              const parsed =
                calc?.annualHoursCombined != null && calc.annualHoursCombined !== ''
                  ? parseFloat(calc.annualHoursCombined)
                  : '';
              const input = (
                <NumberInput
                  label={
                    isInternalView ? (
                      <LabelWithInfo
                        label="Durchschnittliche Jahresarbeitsstunden ohne KANÜ"
                        info="Verfahrene Stunden der Lohnbelegschaft plus verfahrene Stunden der Verwaltung, geteilt durch die Gesamtzahl der eigenen Mitarbeiter. Leihkräfte und der KANÜ-Wert bleiben außen vor. Automatisch berechnet."
                      />
                    ) : (
                      'Durchschnittliche Jahresarbeitsstunden ohne KANÜ'
                    )
                  }
                  value={parsed}
                  readOnly
                  disabled
                  leftSection={warning ? WARNING_ICON : undefined}
                  styles={{
                    input: warning
                      ? { ...WARNING_INPUT_STYLES, pointerEvents: 'none' }
                      : undefined,
                  }}
                  {...DECIMAL_INPUT_PROPS}
                />
              );
              return warning ? <FieldWarningTooltip warning={warning}>{input}</FieldWarningTooltip> : input;
            })()}
            <RegisterFieldTab field="annualHoursCombined" label="Durchschnittliche Jahresarbeitsstunden ohne KANÜ" />
            <PreviousValueForField field="annualHoursCombined" />
          </Grid.Col>
        </Grid>
      </FormSubsection>

      {/* ---- VDZ / GCCA section ---- */}
      <FormSubsection title="Betriebsunfälle gemäß VDZ-Arbeitssicherheitswettbewerb (GCCA-Kriterien)">
        <Box style={{ overflowX: 'auto' }}>
          <Table striped="odd" styles={MATRIX_TABLE_STYLES}>
            {tableHead}
            <Table.Tbody>
              <Table.Tr>
                <Table.Td><Text size="sm">Mit mindestens 1 Ausfalltag</Text></Table.Td>
                {inputCells('accidentsLostDay', 'integer', 'Mit mindestens 1 Ausfalltag')}
                <Table.Td style={{ textAlign: 'right' }}>
                  <Text size="sm">{fmtInt(calc?.accidentsLostDayTotal)}</Text>
                  <RegisterFieldTab
                    field="accidentsLostDayTotal"
                    label="Mit mindestens 1 Ausfalltag – Gesamt"
                  />
                  <PreviousValueForField field="accidentsLostDayTotal" />
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Text size="sm">Arbeitsausfalltage zu Unfällen mit mindestens 1 Ausfalltag</Text></Table.Td>
                {inputCells('lostWorkDays', 'integer', 'Arbeitsausfalltage zu Unfällen mit mindestens 1 Ausfalltag')}
                <Table.Td style={{ textAlign: 'right' }}>
                  <Text size="sm">{fmtInt(calc?.lostWorkDaysTotal)}</Text>
                  <RegisterFieldTab
                    field="lostWorkDaysTotal"
                    label="Arbeitsausfalltage zu Unfällen mit mindestens 1 Ausfalltag – Gesamt"
                  />
                  <PreviousValueForField field="lostWorkDaysTotal" />
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Box>

        {/* K9/D-12: zeilenbezogener Hinweis, genau EINMAL unter der Tabelle. Die drei Felder der
            Zeile "Mit mindestens 1 Ausfalltag" bleiben bewusst unmarkiert -- an ihnen staenden
            sonst drei identische Hinweise nebeneinander. D-12 weicht damit bewusst vom
            Kundenwortlaut ("fuer die Felder in Zeile 3") ab, weil der Hinweis fachlich das Werk
            als Ganzes meint; die Bestaetigung beim naechsten Kundenabgleich steht noch aus.
            Der Text kommt vom Server (Plan 20-06) und wird hier nicht dupliziert; er wird als
            React-Textkind gerendert und damit automatisch escapt (T-20-08-01). */}
        {warnings.accidentsLostDayTotal ? (
          <Alert
            mt="md"
            color="yellow"
            variant="light"
            withCloseButton={false}
            icon={<IconAlertTriangle size={18} />}
          >
            {warnings.accidentsLostDayTotal}
          </Alert>
        ) : null}

        {/* K15/K16: MTI links, Unfallhäufigkeitsrate rechts, beide auf einem Drittel Breite.
            Die dritte Drittel-Spalte bleibt bewusst leer. */}
        <Grid mt="md">
          <Grid.Col span={{ base: 12, md: 4 }}>
            <ValidatedNumberInput
              form={form}
              field="mtiOwnEmployees"
              kind="integer"
              align="left"
              label="Ereignisse mit Arztbesuch ohne Ausfalltage (MTI: Medical treatment injuries)"
              disabled={disabled}
              {...INTEGER_INPUT_PROPS}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <NumberInput
              /* D-17: Berechnungshinweis nur in der internen Sicht (K7) */
              label={
                isInternalView ? (
                  <LabelWithInfo
                    label="Unfallhäufigkeitsrate eigene Mitarbeiter inkl. KANÜ"
                    info="Unfälle mit mindestens 1 Ausfalltag (Gesamt) ÷ verfahrene Arbeitsstunden (Gesamt) × 1.000.000. Automatisch berechnet."
                  />
                ) : (
                  'Unfallhäufigkeitsrate eigene Mitarbeiter inkl. KANÜ'
                )
              }
              value={calc?.accidentRateOwn != null && calc.accidentRateOwn !== '' ? parseFloat(calc.accidentRateOwn) : ''}
              readOnly
              disabled
              {...DECIMAL_INPUT_PROPS}
            />
            <PreviousValueForField field="accidentRateOwn" />
          </Grid.Col>
        </Grid>
      </FormSubsection>

      {/* ---- Indirect employees ---- */}
      {/* K13: Platzhaltertext — der Endtext ("Wer zählt zu indirekten Beschäftigten") wird von
          Komorek nachgereicht. Offener Kundenpunkt, blockiert die Phase nicht. */}
      <FormSubsection
        title="Betriebsunfälle bei indirekt Beschäftigten (GCCA-Kriterien)"
        description="Platzhalter: Definition „Wer zählt zu indirekten Beschäftigten“ — Endtext steht beim Kunden aus."
      >
        <Grid>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <ValidatedNumberInput
              form={form}
              field="indirectHoursWorked"
              fieldLabel="Indirekt Beschäftigte – Verfahrene Arbeitsstunden"
              kind="decimal"
              align="left"
              label="Verfahrene Arbeitsstunden"
              disabled={disabled}
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <ValidatedNumberInput
              form={form}
              field="indirectLtis"
              fieldLabel="Indirekt Beschäftigte – Unfälle mit mindestens 1 Ausfalltag (LTIs)"
              kind="integer"
              align="left"
              label="Unfälle mit mindestens 1 Ausfalltag (LTIs)"
              disabled={disabled}
              {...INTEGER_INPUT_PROPS}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <NumberInput
              /* D-17: Berechnungshinweis nur in der internen Sicht (analog K4/K7) */
              label={
                isInternalView ? (
                  <LabelWithInfo
                    label="Unfallhäufigkeitsrate indirekt Beschäftigte"
                    info="Unfälle mit mindestens 1 Ausfalltag (LTIs) ÷ verfahrene Arbeitsstunden × 1.000.000. Automatisch berechnet."
                  />
                ) : (
                  'Unfallhäufigkeitsrate indirekt Beschäftigte'
                )
              }
              value={calc?.accidentRateIndirect != null && calc.accidentRateIndirect !== '' ? parseFloat(calc.accidentRateIndirect) : ''}
              readOnly
              disabled
              {...DECIMAL_INPUT_PROPS}
            />
            <PreviousValueForField field="accidentRateIndirect" />
          </Grid.Col>
        </Grid>
      </FormSubsection>

      {/* B-03/B-04/B-05: neue eigenstaendige Section zwischen indirekt Beschaeftigten und
          Unfalltote. Gepoolte Formel (eigene + indirekt Beschaeftigte), NICHT die Summe der beiden
          Raten oben — Raten mit unterschiedlichen Nennern sind nicht addierbar (siehe Backend
          AccidentCalculator::calculate()). Melder sehen das Feld (B-05); nur der Erklaer-Tooltip
          bleibt der internen Sicht vorbehalten (D-17-Muster), kein isInternalView-Gate um die
          Section selbst (D-16 gilt exklusiv fuer annualHoursTotal). Keine Warnschwelle, kein
          Hinweis-Tooltip (B-07). */}
      <FormSubsection title="Unfallhäufigkeitsrate über alle Beschäftigten">
        <Grid>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <NumberInput
              label={
                isInternalView ? (
                  <LabelWithInfo
                    label="Unfallhäufigkeitsrate gesamt"
                    info="(Unfälle mit mindestens 1 Ausfalltag inkl. KANÜ + Unfälle indirekt Beschäftigter) ÷ (verfahrene Arbeitsstunden inkl. KANÜ + verfahrene Arbeitsstunden indirekt Beschäftigter) × 1.000.000. Automatisch berechnet."
                  />
                ) : (
                  'Unfallhäufigkeitsrate gesamt'
                )
              }
              value={calc?.accidentRateTotal != null && calc.accidentRateTotal !== '' ? parseFloat(calc.accidentRateTotal) : ''}
              readOnly
              disabled
              {...DECIMAL_INPUT_PROPS}
            />
            <PreviousValueForField field="accidentRateTotal" />
          </Grid.Col>
        </Grid>
      </FormSubsection>

      {/* ---- Fatalities ---- */}
      <FormSubsection title="Unfalltote">
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="fatalitiesWage"
              fieldLabel="Unfalltote – Lohnbelegschaft"
              kind="integer"
              align="left"
              label="Lohnbelegschaft"
              disabled={disabled}
              {...INTEGER_INPUT_PROPS}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="fatalitiesAdmin"
              fieldLabel="Unfalltote – Verwaltung"
              kind="integer"
              align="left"
              label="Verwaltung"
              disabled={disabled}
              {...INTEGER_INPUT_PROPS}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="fatalitiesTemp"
              fieldLabel="Unfalltote – Kräfte aus Arbeitnehmerüberlassung"
              kind="integer"
              align="left"
              label="Kräfte aus Arbeitnehmerüberlassung"
              disabled={disabled}
              {...INTEGER_INPUT_PROPS}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="fatalitiesCommuting"
              fieldLabel="Unfalltote – Wegeunfälle (der zuvor genannten)"
              kind="integer"
              align="left"
              label="Wegeunfälle (der zuvor genannten)"
              disabled={disabled}
              {...INTEGER_INPUT_PROPS}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="indirectFatalities"
              fieldLabel="Unfalltote – Indirekte Beschäftigte"
              kind="integer"
              align="left"
              label="Indirekte Beschäftigte"
              description="GCCA: Subcontractors, z.B. Mitarbeiter in Werkverträgen"
              disabled={disabled}
              {...INTEGER_INPUT_PROPS}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="mtiThirdParties"
              kind="integer"
              align="left"
              label="Unfalltote bei Dritten / sonstigen Personen"
              description="GCCA: Third Parties, z.B. Postbote, Besucher"
              disabled={disabled}
              {...INTEGER_INPUT_PROPS}
            />
          </Grid.Col>
        </Grid>
      </FormSubsection>
    </Stack>
  );
}
